import React, { useEffect, useState } from "react";
import InfoBox from "../components/InfoBox";
import LocationTabs from "../components/LocationTabs";
import InputField from "../components/InputField";
import BuildingTypeInput from "../components/BuildingTypeInput";
import ToggleSwitch from "../components/ToggleSwitch";
import DateRangeInput from "../components/DateRangeInput";
import InputAutocompleteAddress from "../components/InputAutocompleteAddress";
import "./facility.css";
import { useTranslation } from "react-i18next";
import DropdownWithInput from "../components/DropdownWithInputField";
import editIcon from "../../../assets/design-revamp-company/add-edit/editIcon.svg";
import deleteIcon from "../../../assets/design-revamp-company/add-edit/deleteIcon.svg";
import saveIcon from "../../../assets/design-revamp-company/add-edit/saveIcon.svg";
import { getFirstBusiness } from "../../../apis/Business/Business";
import { useUserAccess } from "../../../components/WriteAccessContext";

const Facility = ({
  initialData,
  isNewFacility,
  onFormDataChange,
  handleSave,
  types,
  setTypes,
  errorMessage,
  setCurrentName,
  setCutOffAddress,
  setPercentage,
  handleDelete,
}) => {
  const [initialYearStart, setInitialYearStart] = useState("");
  const [initialYearEnd, setInitialYearEnd] = useState("");
  useEffect(() => {
    getFirstBusiness({
      setAccountingYearStart: setInitialYearStart,
      setAccountingYearEnd: setInitialYearEnd,
    });
  }, []);
  const { t } = useTranslation();
  const [name, setName] = useState(initialData.name);
  const [addressLine1, setAddressLine1] = useState(initialData.addressLine1);
  const [addressLine2, setAddressLine2] = useState(initialData.addressLine2);
  const [type, setType] = useState(initialData.type);
  const [ownership, setOwnership] = useState(initialData.ownership);
  const [ownershipPerc, setOwnershipPerc] = useState(initialData.ownershipPerc);
  const [fullTimeEmployees, setFullTimeEmployees] = useState(
    initialData.fullTimeEmployees
  );
  const [baseline, setBaseline] = useState(
    isNewFacility
      ? initialData.baseline
      : initialData.baseline === true
      ? t("Yes")
      : t("No")
  );
  const [revenue, setRevenue] = useState(initialData.revenue);
  const [yearStart, setYearStart] = useState(initialYearStart);
  const [yearEnd, setYearEnd] = useState(initialYearEnd);
  const [city, setCity] = useState(initialData.city);
  const [state, setState] = useState(initialData.state);
  const [country, setCountry] = useState(initialData.country);
  const [isEditing, setIsEditing] = useState(true);
  const [isDisabled, setIsDisabled] = useState(false);
  const [displayTextOwnership, setDisplayTextOwnership] = useState(false);
  const { userAccess } = useUserAccess();

  useEffect(() => {
    setYearStart(initialYearStart);
    setYearEnd(initialYearEnd);
  }, [initialYearStart, initialYearEnd]);

  const handlePercentageChange = (newOwnership) => {
    if (newOwnership === "Selvejet" || newOwnership === "Wholly owned asset") {
      setOwnershipPerc("100");
      setIsDisabled(true);
      setDisplayTextOwnership(false);
    } else if (
      newOwnership === "Leaset aktiv finans-/kapitalleasing" ||
      newOwnership === "Leased asset - finance/capital lease"
    ) {
      setOwnershipPerc("100");
      setIsDisabled(true);
      setDisplayTextOwnership(false);
    } else if (
      newOwnership === "Leaset aktiv - operationel leasing" ||
      newOwnership === "Leased asset - operating lease"
    ) {
      setOwnershipPerc("0");
      setIsDisabled(true);
      setDisplayTextOwnership(true);
    } else {
      setOwnershipPerc("");
      setIsDisabled(false);
      setDisplayTextOwnership(false);
    }
    setOwnership(newOwnership);
    onFormDataChange({
      name,
      addressLine1,
      addressLine2,
      type,
      ownership,
      ownershipPerc,
      fullTimeEmployees,
      baseline,
      revenue,
      yearStart,
      yearEnd,
      city,
      state,
      country,
    });
  };

  const handleInputChange = (field, value) => {
    switch (field) {
      case "addressLine1":
        const addressParts = value.split(", ");
        const country = addressParts.pop();
        const [cutOffAddress, addressLine2, city, state] = addressParts;
        setCutOffAddress(cutOffAddress);
        setAddressLine1(value);
        setAddressLine2(addressLine2 || "");
        setCity(city || "");
        setState(state || "");
        // setPostalCode(postalCode || "");
        setCountry(country || "");
        break;
      case "ownership":
        handlePercentageChange(value);
        break;
      default:
        switch (field) {
          case "name":
            setName(value);
            setCurrentName(value);
            break;
          case "type":
            setType(value);
            break;
          case "fullTimeEmployees":
            setFullTimeEmployees(value);
            break;
          case "baseline":
            setBaseline(value);
            break;
          case "revenue":
            setRevenue(value);
            break;
          case "yearStart":
            setYearStart(value);
            break;
          case "yearEnd":
            setYearEnd(value);
            break;
          default:
            break;
        }
        break;
    }
    onFormDataChange({
      name: field === "name" ? value : name,
      addressLine1: field === "addressLine1" ? value : addressLine1,
      addressLine2,
      type: field === "type" ? value : type,
      ownership: field === "ownership" ? value : ownership,
      ownershipPerc: ownershipPerc,
      fullTimeEmployees:
        field === "fullTimeEmployees" ? value : fullTimeEmployees,
      baseline: field === "baseline" ? value : baseline,
      revenue: field === "revenue" ? value : revenue,
      yearStart: field === "yearStart" ? value : yearStart,
      yearEnd: field === "yearEnd" ? value : yearEnd,
      city,
      state,
      country,
    });
  };

  const dropdownOwnership = [
    { id: 1, name: t("Wholly owned asset"), value: t("Wholly owned asset") },
    {
      id: 2,
      name: t("Group company/subsidiary/franchise"),
      value: t("Group company/subsidiary/franchise"),
    },
    {
      id: 3,
      name: t("Joint venture/partnership"),
      value: t("Joint venture/partnership"),
    },
    {
      id: 4,
      name: t("Associated/affiliated company"),
      value: t("Associated/affiliated company"),
    },
    {
      id: 5,
      name: t("Leased asset - finance/capital lease"),
      value: t("Leased asset - finance/capital lease"),
    },
    {
      id: 6,
      name: t("Leased asset - operating lease"),
      value: t("Leased asset - operating lease"),
    },
  ];

  const getLocationTabs = () => {
    const tabs = [];

    if (country) tabs.push(country);
    if (name) {
      const nameParts = name.split(" ");
      if (nameParts.length > 1) {
        tabs.push(nameParts[1]);
      } else {
        tabs.push(nameParts[0]);
      }
    }
    if (types && types.length > 0) {
      const checkedTypes = types.filter((t) => t.checked);
      if (checkedTypes.length > 0) {
        tabs.push(checkedTypes.map((t) => t.name_en).join(" / "));
      }
    }
    if (addressLine1) {
      const addressParts = addressLine1.split(",");
      if (addressParts.length > 3) {
        tabs.push(addressParts[3]);
      } else {
        tabs.push(addressParts[addressParts.length - 1]);
      }
    }

    return tabs;
  };

  useEffect(() => {
    setPercentage(ownershipPerc);
  }, [ownershipPerc, setPercentage]);

  return (
    <div className="facility-container">
      <div className="header">
        <h2>{t("ADD_FACILITY")}</h2>
        {/* {!isNewFacility && (
          <div className="header-buttons">
            {isEditing ? (
              <button
                className="close-button"
                onClick={() => setIsEditing(false)}
              >
                <img src={deleteIcon} alt="" width={28} height={27} />
              </button>
            ) : (
              userAccess === "write" && (
                <div style={{ display: "flex" }}>
                  <button
                    className="edit-button"
                    onClick={() => setIsEditing(true)}
                  >
                    <img src={editIcon} alt="" width={28} height={27} />
                  </button>
                  <button
                    className="close-button"
                    onClick={handleDelete}
                    style={{ marginLeft: 0 }}
                  >
                    <img src={deleteIcon} alt="" width={28} height={27} />
                  </button>
                </div>
              )
            )}
          </div>
        )} */}
      </div>
      <InfoBox t={t} text={"FACILITY_TEXT"} />
      <LocationTabs locations={getLocationTabs()} />
      <div className="form-container">
        <InputField
          label={`${t("COMPANY_NAME")}*`}
          value={name}
          onChange={(value) => handleInputChange("name", value)}
          isEditing={isEditing}
        />
        <InputAutocompleteAddress
          label={`${t("ADDRESS")}*`}
          value={addressLine1}
          onChange={(value) => handleInputChange("addressLine1", value)}
          isEditing={isEditing}
          errorMessage={errorMessage.address && errorMessage.address[0]}
        />
        <BuildingTypeInput
          value={type}
          onChange={(value) => handleInputChange("type", value)}
          t={t}
          isEditing={isEditing}
          types={types}
          setTypes={setTypes}
        />
        <div className="form-row">
          <div>
            <DropdownWithInput
              label={`${t("Ownership")}*`}
              options={dropdownOwnership}
              valueDropdown={ownership}
              onChangeDropdown={(value) =>
                handleInputChange("ownership", value)
              }
              isEditing={isEditing}
              valueInput={ownershipPerc}
              onChangeInput={(value) => setOwnershipPerc(value)}
              isDisabled={isDisabled}
            />
            {displayTextOwnership && (
              <p style={{ fontSize: "12px", color: "#2c5b47" }}>
                {t("LEASE_OWNERSHIP_TEXT")}
              </p>
            )}
          </div>

          <InputField
            label={`${t("Full Time Employees (FTE)")}*`}
            value={fullTimeEmployees}
            onChange={(value) => handleInputChange("fullTimeEmployees", value)}
            isEditing={isEditing}
            errorMessage={
              errorMessage.fullTimeEmployees &&
              errorMessage.fullTimeEmployees[0]
            }
          />
        </div>
        <div className="form-row">
          <ToggleSwitch
            label={`${t("Baseline")}*`}
            options={[t("Yes"), t("No")]}
            value={baseline}
            onChange={(value) => handleInputChange("baseline", value)}
            isEditing={isEditing}
          />
          <InputField
            label={`${t("Revenue per year (DKK)")}*`}
            value={revenue}
            onChange={(value) => handleInputChange("revenue", value)}
            isEditing={isEditing}
          />
        </div>
        <DateRangeInput
          label={t("Accounting year")}
          startDate={yearStart}
          endDate={yearEnd}
          onChangeStart={(value) => handleInputChange("yearStart", value)}
          onChangeEnd={(value) => handleInputChange("yearEnd", value)}
          isEditing={isEditing}
          errorMessage={errorMessage.yearEnd?.[0]}
        />
      </div>
      {userAccess === "write" && (
        <div className="footer">
          <button className="save-button" onClick={handleSave}>
            <img src={saveIcon} alt="" width={28} height={28} />
          </button>
        </div>
      )}
    </div>
  );
};

export default Facility;
